"use client"

import { useEffect } from 'react'
import { useCommandPaletteStore } from '@/stores/command-palette-store'
import { useCommandShortcutActions } from '@/hooks/use-command-shortcut-actions'
import { useCaptureStore } from '@/stores/capture-store'
import { useLayoutStore } from '@/stores/layout-store'

export function useKeyboardShortcuts() {
  const toggle = useCommandPaletteStore((s) => s.toggle)
  const {
    openTaskActionShortcut,
    pauseTaskTimer,
    endTaskTimer,
    pausePomodoroTimer,
    endPomodoroTimer,
  } = useCommandShortcutActions()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey
      const isShift = e.shiftKey
      const isAlt = e.altKey

      // Cmd/Ctrl+K → toggle command palette. The shift guard matters: without
      // it every Cmd+Shift+<key> chord starting with K opened the palette too,
      // so a chord would fire this *and* its own handler.
      if (isMod && !isShift && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        toggle()
        return
      }

      // Cmd/Ctrl+\ → collapse or restore the sidebar. Notion's chord, and it
      // is deliberately above the input guard: hiding the navigation is safe
      // to do mid-sentence, and wanting the width back is most likely exactly
      // when you are typing.
      if (isMod && e.key === '\\') {
        e.preventDefault()
        useLayoutStore.getState().toggleSidebar()
        return
      }

      // Skip remaining shortcuts when focused in input/textarea
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      // Cmd/Ctrl+B → brain dump
      if (isMod && !isShift && e.key.toLowerCase() === 'b') {
        e.preventDefault()
        useCaptureStore.getState().open()
        return
      }

      if (isMod && isShift && !isAlt && e.key.toLowerCase() === 't') {
        e.preventDefault()
        openTaskActionShortcut('start-task-timer')
        return
      }

      if (isMod && !isShift && isAlt && e.key.toLowerCase() === 't') {
        e.preventDefault()
        void pauseTaskTimer()
        return
      }

      if (isMod && isShift && isAlt && e.key.toLowerCase() === 't') {
        e.preventDefault()
        void endTaskTimer()
        return
      }

      if (isMod && isShift && !isAlt && e.key.toLowerCase() === 'p') {
        e.preventDefault()
        openTaskActionShortcut('start-pomodoro-timer')
        return
      }

      if (isMod && !isShift && isAlt && e.key.toLowerCase() === 'p') {
        e.preventDefault()
        void pausePomodoroTimer()
        return
      }

      if (isMod && isShift && isAlt && e.key.toLowerCase() === 'p') {
        e.preventDefault()
        void endPomodoroTimer()
        return
      }

      if (isMod && isShift && !isAlt && e.key.toLowerCase() === 'c') {
        e.preventDefault()
        openTaskActionShortcut('mark-task-complete')
        return
      }

      if (isMod && isShift && !isAlt && e.key.toLowerCase() === 's') {
        e.preventDefault()
        openTaskActionShortcut('schedule-task')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [endPomodoroTimer, endTaskTimer, openTaskActionShortcut, pausePomodoroTimer, pauseTaskTimer, toggle])
}
